import React, { useEffect, useState } from 'react';
import classes from "./PredictionGame2.module.css";
import { FaArrowLeft, FaCircle, FaCheck, FaPenToSquare, FaTrophy } from 'react-icons/fa6';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from "moment-timezone";
import { getPredictionLeaderboardApi, getUserPredictionsApi, matchDetailsApi } from '../../http/http';
import { useDispatch, useSelector } from 'react-redux';
import MatchEvents2 from '../../component/NewComponents/MatchEvents2';
import SmallLoader from '../../component/SmallLoader';
import { removeUserDetails } from '../../store/slices/userSlice';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/* ─── GOAL_TIMES map for display label ─── */
const GOAL_TIME_LABELS = {
  "0-15": "0 – 15 min",
  "16-30": "16 – 30 min",
  "31-45": "31 – 45 min",
  "46-60": "46 – 60 min",
  "61-75": "61 – 75 min",
  "76-90": "76 – 90 min",
  "none": "No Goal",
};

/* ─── Safe parse ─── */
const safeParse = (raw, fb = []) => {
  try {
    const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(p) ? p : fb;
  } catch { return fb; }
};

/* ─── Medal colours for top 3 ─── */
const MEDAL = { 1: "🥇", 2: "🥈", 3: "🥉" };

/* ════════════════════════════════════
   COMPONENT
════════════════════════════════════ */
const PredictionGame2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userDetails } = useSelector(s => s?.persisted?.userSlice);

  const { t } = useTranslation();

  const { selectedLanguage } = useSelector((state) => state.languageSlice);

  const [matchDetails, setMatchDetails] = useState(null);
  const [matchDetails2, setMatchDetails2] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [tab, setTab] = useState("predictions");
  const [loading, setLoading] = useState(true);
  const [loader, setLoader] = useState(true);
  const [time, setTime] = useState(null);
  const [display, setDisplay] = useState("block");

  /* ── fetch functions ── */
  const fetchMatchDetails = async () => {
    try {
      const r = await matchDetailsApi(location?.state?.match_fixture_id, userDetails?.token);
      setMatchDetails(r?.data);
      if (r?.data?.match_details) setMatchDetails2(JSON.parse(r?.data?.match_details));
    } catch (e) {
      if (e?.response?.status === 403) {
        dispatch(removeUserDetails()); toast.error(t("2")); navigate('/');
      } else {
        toast.error(e?.response?.data?.message || t("71"));
      }
    }
  };

  const fetchUserPredictions = async () => {
    try {
      const r = await getUserPredictionsApi(location?.state?.match_fixture_id, userDetails?.token);
      setPredictions(safeParse(r?.data?.[0]?.predictions));
    } catch (e) { console.log(e); }
  };

  const fetchLeaderboard = async () => {
    try {
      const r = await getPredictionLeaderboardApi(location?.state?.match_fixture_id, userDetails?.token);
      setLeaderboard(r?.data?.leaderboard ?? []);
      setUserRank(r?.data?.userRank?.[0]);
    } catch (e) { console.log(e); }
  };

  const fetchData = async (load) => {
    if (load) setLoading(true);
    setLoader(true);
    await Promise.all([fetchMatchDetails(), fetchUserPredictions(), fetchLeaderboard()]);
    setLoading(false);
    setLoader(false);
  };

  useEffect(() => {
    if (location?.state?.match_fixture_id) fetchData(true);
    else navigate("/home");
  }, [location?.state?.match_fixture_id]);

  useEffect(() => {
    if (!loader) {
      const id = setInterval(() => { if (!document.hidden) fetchData(false); }, 10000);
      return () => clearInterval(id);
    }
  }, [loader]);

  /* ── countdown timer ── */
  const matchTimer = (dt) => {
    try {
      const left = moment.duration(moment.utc(dt).diff(moment()));
      if (left.asMilliseconds() <= 0) { setDisplay("none"); return; }
      const h = Math.floor(left.asHours());
      setTime(`${h}h ${left.minutes()}m ${left.seconds()}s`);
    } catch (e) { console.log(e); }
  };

  useEffect(() => {
    if (matchDetails?.match_status === "NS" && display !== "none") {
      const id = setInterval(() => matchTimer(matchDetails.match_date_time), 1000);
      return () => clearInterval(id);
    }
  }, [matchDetails, display]);

  /* ── match status label ── */
  const renderStatus = () => {
    const s = matchDetails?.match_status;
    const elapsed = matchDetails2?.fixture?.status?.elapsed;
    const extra = matchDetails2?.fixture?.status?.extra;
    const liveIcon = <span className={classes.liveDot} />;
    if (["FT", "AET", "PEN"].includes(s)) return { label: t("100"), live: false };
    if (s === "SUSP") return { label: t("101"), live: false };
    if (s === "PST") return { label: t("102"), live: false };
    if (s === "CANC") return { label: t("103"), live: false };
    if (["ABD", "AWD", "WO"].includes(s)) return { label: t("104"), live: false };
    if (s === "HT") return { label: `${t("105")} ${elapsed}'`, live: true };
    if (s === "ET") return { label: `${elapsed}' ${t("106")} +${extra}'`, live: true };
    if (s === "P") return { label: `${t("107")} ${elapsed}'`, live: true };
    if (["1H", "2H", "LIVE"].includes(s)) return { label: `${elapsed}'`, live: true };
    return null;
  };

  /* ── extract typed predictions ── */
  const getPred = (key) => predictions.find(p => p[key])?.[key];
  const scorers = getPred('scorers') ?? [];
  const halfTime = getPred('halfTime') ?? null;
  const winner = getPred('winner') ?? null;

  // firstGoal can be a plain string "0-15" (before match ends)
  // OR an object { value:"0-15", point:0, message:"..." } (after scoring)
  const firstGoalRaw = getPred('firstGoal') ?? null;
  const firstGoalValue = typeof firstGoalRaw === 'string'
    ? firstGoalRaw
    : (firstGoalRaw?.value ?? null);
  const firstGoalResult = (typeof firstGoalRaw === 'object' && firstGoalRaw !== null)
    ? firstGoalRaw
    : null;
  const firstGoal = firstGoalValue; // alias — keeps existing JSX checks working

  const isNotStarted = matchDetails?.match_status === "NS";
  const statusObj = renderStatus();
  const homeGoals = matchDetails2?.goals?.home;
  const awayGoals = matchDetails2?.goals?.away;
  const showScore = homeGoals !== undefined && homeGoals !== null;

  if (loading) return (
    <div className={classes.page}>
      <div className={classes.loaderScreen}><SmallLoader /></div>
    </div>
  );

  return (
    <div className={classes.page}>

      {/* ══ STICKY HEADER ══ */}
      <div className={classes.stickyHeader}>

        {/* top nav */}
        <div className={classes.topNav}>
          <button className={classes.backBtn} onClick={() => navigate("/home")}>
            <FaArrowLeft />
          </button>
          <span className={classes.topNavTitle}>{t("167")}</span>
          {isNotStarted && (
            <button
              className={classes.editBtn}
              onClick={() => navigate("/create-prediction", {
                state: { match_fixture_id: matchDetails?.match_fixture_id, edit: true }
              })}
            >
              <FaPenToSquare />
              {t("168")}
            </button>
          )}
          {!isNotStarted && <div style={{ width: 64 }} />}
        </div>

        {/* match banner */}
        <div className={classes.matchBanner}>
          <div className={classes.matchBannerGlow} />

          {/* status chip */}
          {statusObj && (
            <div className={`${classes.statusChip} ${statusObj.live ? classes.statusChipLive : classes.statusChipFt}`}>
              {statusObj.live && <span className={classes.liveDot} />}
              {statusObj.label}
            </div>
          )}

          {/* teams + score */}
          <div className={classes.matchTeamsRow}>
            <div className={classes.matchTeam}>
              <div className={classes.matchLogoRing}>
                <img src={matchDetails?.home_team_image} alt="" className={classes.matchLogoImg} />
              </div>
              <span className={classes.matchTeamName}>{matchDetails?.home_team_name}</span>
            </div>

            <div className={classes.matchScoreBlock}>
              {showScore ? (
                <div className={classes.scoreRow}>
                  <span className={classes.scoreNum}>{homeGoals}</span>
                  <span className={classes.scoreDash}>—</span>
                  <span className={classes.scoreNum}>{awayGoals}</span>
                </div>
              ) : (
                <span className={classes.vsText}>{t("169")}</span>
              )}
              <span className={classes.matchKo}>
                {moment.utc(matchDetails?.match_date_time).local().format("ddd DD MMM · HH:mm")}
              </span>
              {isNotStarted && display !== "none" && time && (
                <span className={classes.timerChip}>⏱ {time}</span>
              )}
            </div>

            <div className={classes.matchTeam}>
              <div className={classes.matchLogoRing}>
                <img src={matchDetails?.away_team_image} alt="" className={classes.matchLogoImg} />
              </div>
              <span className={classes.matchTeamName}>{matchDetails?.away_team_name}</span>
            </div>
          </div>
        </div>

        {/* tabs */}
        <div className={classes.tabsRow}>
          {[
            { key: "predictions", label: t("170") },
            { key: "leaderboard", label: t("171") },
            { key: "events", label: t("172") },
          ].map(t => (
            <button
              key={t.key}
              type="button"
              className={`${classes.tabBtn} ${tab === t.key ? classes.tabBtnActive : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {tab === t.key && <span className={classes.tabUnderline} />}
            </button>
          ))}
        </div>
      </div>

      {/* ══ CONTENT ══ */}
      <div className={classes.content}>

        {/* ─ My Predictions tab ─ */}
        {tab === "predictions" && (
          <div className={classes.predsPanel}>

            {predictions.length === 0 ? (
              <div className={classes.emptyState}>
                <span>🎯</span>
                <p>{t("173")}</p>
              </div>
            ) : (
              <>
                {/* ── 1. Goal Scorers ── */}
                {scorers.length > 0 && (
                  <div className={classes.predCard}>
                    <div className={classes.predCardHead}>
                      <span className={classes.predCardEmoji}>⚽</span>
                      <div>
                        <p className={classes.predCardTitle}>{t("174")}</p>
                        <p className={classes.predCardSub}>{t("175")}</p>
                      </div>
                    </div>
                    <div className={classes.scorersList}>
                      {scorers.map((scorer, i) => (
                        <div key={scorer.id ?? i} className={classes.scorerRow}>
                          <div className={classes.scorerAvatar}>
                            {scorer.photo
                              ? <img src={scorer.photo} alt={scorer.name} className={classes.scorerAvatarImg} />
                              : <span className={classes.scorerAvatarNum}>{scorer.number ?? (i + 1)}</span>
                            }
                            <span className={classes.scorerBadge}>{i + 1}</span>
                          </div>
                          <div className={classes.scorerInfo}>
                            <span className={classes.scorerName}>{scorer.name ?? '—'}</span>
                            {scorer.team && <span className={classes.scorerTeam}>{scorer.team}</span>}
                          </div>
                          {/* result if available */}
                          {scorer.message !== undefined && scorer.message !== "" && (
                            <div className={`${classes.scorerResult} ${scorer.point > 0 ? classes.scorerResultWin : classes.scorerResultLoss}`}>
                              {scorer.point > 0 ? <FaCheck /> : "✗"}
                              <span>+{scorer.point ?? 0} {t("176")}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── 2. First Goal Time ── */}
                {firstGoal && (
                  <div className={classes.predCard}>
                    <div className={classes.predCardHead}>
                      <span className={classes.predCardEmoji}>⏱</span>
                      <div>
                        <p className={classes.predCardTitle}>{t("177")}</p>
                        <p className={classes.predCardSub}>{t("178")}</p>
                      </div>
                    </div>
                    <div className={classes.predAnswer}>
                      <span className={classes.predAnswerText}>
                        {GOAL_TIME_LABELS[firstGoal] ?? firstGoal}
                      </span>
                    </div>
                    {/* result feedback after match ends */}
                    {firstGoalResult?.message && (
                      <div className={`${classes.predResult} ${firstGoalResult.point > 0 ? classes.predResultWin : classes.predResultLoss}`}>
                        <span>{selectedLanguage == 0 ? firstGoalResult.message : firstGoalResult?.messageSwahili}</span>
                        <span className={classes.predResultPts}>+{firstGoalResult.point ?? 0} {t("179")}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* ── 3. Halftime Score ── */}
                {halfTime && (
                  <div className={classes.predCard}>
                    <div className={classes.predCardHead}>
                      <span className={classes.predCardEmoji}>🔔</span>
                      <div>
                        <p className={classes.predCardTitle}>{t("180")}</p>
                        <p className={classes.predCardSub}>{t("181")}</p>
                      </div>
                    </div>

                    <div className={classes.htScoreDisplay}>
                      {/* home side */}
                      <div className={classes.htScoreTeam}>
                        {halfTime.homeTeamImage && (
                          <img src={halfTime.homeTeamImage} alt="" className={classes.htScoreLogo} />
                        )}
                        <span className={classes.htScoreTeamName}>{halfTime.homeTeamName}</span>
                      </div>

                      <div className={classes.htScoreNums}>
                        <span className={classes.htScoreNum}>{halfTime.homeTeamScore}</span>
                        <span className={classes.htScoreDash}>–</span>
                        <span className={classes.htScoreNum}>{halfTime.awayTeamScore}</span>
                      </div>

                      {/* away side */}
                      <div className={`${classes.htScoreTeam} ${classes.htScoreTeamRight}`}>
                        {halfTime.awayTeamImage && (
                          <img src={halfTime.awayTeamImage} alt="" className={classes.htScoreLogo} />
                        )}
                        <span className={classes.htScoreTeamName}>{halfTime.awayTeamName}</span>
                      </div>
                    </div>

                    {/* result feedback if available */}
                    {halfTime.message !== undefined && halfTime.message !== "" && (
                      <div className={`${classes.predResult} ${halfTime.point > 0 ? classes.predResultWin : classes.predResultLoss}`}>
                        <span>{selectedLanguage == 0 ? halfTime.message : halfTime?.messageSwahili}</span>
                        <span className={classes.predResultPts}>+{halfTime.point ?? 0} {t("176")}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* ── 4. Match Winner ── */}
                {winner && (
                  <div className={classes.predCard}>
                    <div className={classes.predCardHead}>
                      <span className={classes.predCardEmoji}>🏆</span>
                      <div>
                        <p className={classes.predCardTitle}>{t("182")}</p>
                        <p className={classes.predCardSub}>{t("183")}</p>
                      </div>
                    </div>

                    <div className={classes.winnerDisplay}>
                      {winner.teamName === 'Draw' ? (
                        <div className={classes.winnerDrawRow}>
                          <span className={classes.winnerDrawIcon}>🤝</span>
                          <span className={classes.winnerName}>{t("184")}</span>
                        </div>
                      ) : (
                        <div className={classes.winnerTeamRow}>
                          {/* find logo from matchDetails */}
                          {matchDetails && (
                            <div className={classes.winnerLogoWrap}>
                              <img
                                src={
                                  winner.teamName === matchDetails.home_team_name
                                    ? matchDetails.home_team_image
                                    : matchDetails.away_team_image
                                }
                                alt={winner.teamName}
                                className={classes.winnerLogoImg}
                              />
                            </div>
                          )}
                          <span className={classes.winnerName}>{winner.teamName}</span>
                        </div>
                      )}
                    </div>

                    {/* result feedback if available */}
                    {winner.message !== undefined && winner.message !== "" && (
                      <div className={`${classes.predResult} ${winner.point > 0 ? classes.predResultWin : classes.predResultLoss}`}>
                        <span>{selectedLanguage == 0 ? winner.message : winner?.messageSwahili}</span>
                        <span className={classes.predResultPts}>+{winner.point ?? 0} {t("179")}</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─ Leaderboard tab ─ */}
        {tab === "leaderboard" && (
          <div className={classes.leaderboardPanel}>

            {/* user's own rank card */}
            {userRank && (
              <div className={classes.userRankCard}>
                <div className={classes.userRankLeft}>
                  <span className={classes.userRankMedal}>{MEDAL[userRank.rank] ?? `#${userRank.rank}`}</span>
                  <div className={classes.userRankInfo}>
                    <span className={classes.userRankLabel}>{t("185")}</span>
                    <span className={classes.userRankMsisdn}>{userRank.msisdn}</span>
                  </div>
                </div>
                <div className={classes.userRankPts}>
                  <span className={classes.userRankPtsNum}>{userRank.total_points}</span>
                  <span className={classes.userRankPtsLabel}>{t("179")}</span>
                </div>
              </div>
            )}

            {/* table */}
            <div className={classes.lbTable}>
              <div className={classes.lbHead}>
                <span className={classes.lbColRank}>{t("186")}</span>
                <span className={classes.lbColNumber}>{t("187")}</span>
                <span className={classes.lbColPts}>{t("188")}</span>
              </div>
              {leaderboard.length === 0 && (
                <div className={classes.emptyState}>
                  <span>🏆</span><p>{t("189")}</p>
                </div>
              )}
              {leaderboard.map((row, i) => (
                <div
                  key={i}
                  className={`${classes.lbRow} ${userRank?.msisdn === row.msisdn ? classes.lbRowSelf : ""}`}
                >
                  <span className={classes.lbColRank}>
                    {MEDAL[row.rank] ?? <span className={classes.lbRankNum}>{row.rank}</span>}
                  </span>
                  <span className={classes.lbColNumber}>{row.msisdn}</span>
                  <span className={classes.lbColPts}>{row.total_points}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─ Events tab ─ */}
        {tab === "events" && (
          <div className={classes.eventsGrid}>
            <MatchEvents2
              team_name={matchDetails?.home_team_name}
              data={matchDetails2?.events?.filter(d =>
                d?.team?.name === matchDetails?.home_team_name &&
                d?.type === "Goal" &&
                d?.detail !== "Missed Penalty"
              )}
            />
            <MatchEvents2
              team_name={matchDetails?.away_team_name}
              data={matchDetails2?.events?.filter(d =>
                d?.team?.name === matchDetails?.away_team_name &&
                d?.type === "Goal" &&
                d?.detail !== "Missed Penalty"
              )}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default PredictionGame2;